

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/components/ui/card';
import { Button } from '@repo/ui/components/ui/button';
import { Label } from '@repo/ui/components/ui/label';
import { Input } from '@repo/ui/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/ui/select';
import { Calendar } from '@repo/ui/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@repo/ui/components/ui/popover';
import { Badge } from '@repo/ui/components/ui/badge';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  AuditLogFilters,
  AuditAction,
  AuditSeverity,
  AuditCategory,
} from '@repo/ui/lib/api/types/audit-log.types';
import { cn } from '@repo/ui/lib/utils';

interface AuditLogFiltersPanelProps {
  filters: AuditLogFilters;
  onChange: (filters: Partial<AuditLogFilters>) => void;
  onClear: () => void;
}

export function AuditLogFiltersPanel({
  filters,
  onChange,
  onClear,
}: AuditLogFiltersPanelProps) {
  const [startDate, setStartDate] = useState<Date | undefined>(
    filters.startDate ? new Date(filters.startDate) : undefined
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    filters.endDate ? new Date(filters.endDate) : undefined
  );

  const handleStartDateChange = (date: Date | undefined) => {
    setStartDate(date);
    onChange({ startDate: date?.toISOString() });
  };

  const handleEndDateChange = (date: Date | undefined) => {
    setEndDate(date);
    onChange({ endDate: date?.toISOString() });
  };

  const handleActionChange = (value: string) => {
    if (value === 'all') {
      onChange({ action: undefined });
    } else {
      onChange({ action: value as AuditAction });
    }
  };

  const handleSeverityChange = (value: string) => {
    if (value === 'all') {
      onChange({ severity: undefined });
    } else {
      onChange({ severity: value as AuditSeverity });
    }
  };

  const handleCategoryChange = (value: string) => {
    if (value === 'all') {
      onChange({ category: undefined });
    } else {
      onChange({ category: value as AuditCategory });
    }
  };

  const activeFiltersCount = Object.keys(filters).filter(
    (key) => !['page', 'limit'].includes(key) && filters[key as keyof AuditLogFilters]
  ).length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Filtres avancés</CardTitle>
          {activeFiltersCount > 0 && (
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{activeFiltersCount} filtre(s) actif(s)</Badge>
              <Button variant="ghost" size="sm" onClick={onClear}>
                <X className="h-4 w-4 mr-1" />
                Tout effacer
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {/* User ID Filter */}
          <div className="space-y-2">
            <Label htmlFor="userId">ID Utilisateur</Label>
            <Input
              id="userId"
              type="number"
              placeholder="Filtrer par utilisateur..."
              value={filters.userId || ''}
              onChange={(e) =>
                onChange({ userId: e.target.value ? Number(e.target.value) : undefined })
              }
            />
          </div>

          {/* Action Filter */}
          <div className="space-y-2">
            <Label htmlFor="action">Action</Label>
            <Select
              value={filters.action as string || 'all'}
              onValueChange={handleActionChange}
            >
              <SelectTrigger id="action">
                <SelectValue placeholder="Toutes les actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les actions</SelectItem>
                <SelectItem value={AuditAction.CREATE}>CREATE</SelectItem>
                <SelectItem value={AuditAction.READ}>READ</SelectItem>
                <SelectItem value={AuditAction.UPDATE}>UPDATE</SelectItem>
                <SelectItem value={AuditAction.DELETE}>DELETE</SelectItem>
                <SelectItem value={AuditAction.LOGIN}>LOGIN</SelectItem>
                <SelectItem value={AuditAction.LOGOUT}>LOGOUT</SelectItem>
                <SelectItem value={AuditAction.LOGIN_FAILED}>LOGIN_FAILED</SelectItem>
                <SelectItem value={AuditAction.PAYMENT_SUCCESS}>PAYMENT_SUCCESS</SelectItem>
                <SelectItem value={AuditAction.PAYMENT_FAILED}>PAYMENT_FAILED</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Severity Filter */}
          <div className="space-y-2">
            <Label htmlFor="severity">Sévérité</Label>
            <Select
              value={filters.severity as string || 'all'}
              onValueChange={handleSeverityChange}
            >
              <SelectTrigger id="severity">
                <SelectValue placeholder="Toutes les sévérités" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les sévérités</SelectItem>
                <SelectItem value={AuditSeverity.INFO}>INFO</SelectItem>
                <SelectItem value={AuditSeverity.WARNING}>WARNING</SelectItem>
                <SelectItem value={AuditSeverity.ERROR}>ERROR</SelectItem>
                <SelectItem value={AuditSeverity.CRITICAL}>CRITICAL</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Category Filter */}
          <div className="space-y-2">
            <Label htmlFor="category">Catégorie</Label>
            <Select
              value={filters.category as string || 'all'}
              onValueChange={handleCategoryChange}
            >
              <SelectTrigger id="category">
                <SelectValue placeholder="Toutes les catégories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les catégories</SelectItem>
                <SelectItem value={AuditCategory.USER_ACTION}>USER_ACTION</SelectItem>
                <SelectItem value={AuditCategory.SYSTEM}>SYSTEM</SelectItem>
                <SelectItem value={AuditCategory.SECURITY}>SECURITY</SelectItem>
                <SelectItem value={AuditCategory.PERFORMANCE}>PERFORMANCE</SelectItem>
                <SelectItem value={AuditCategory.PAYMENT}>PAYMENT</SelectItem>
                <SelectItem value={AuditCategory.DATA}>DATA</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Resource Filter */}
          <div className="space-y-2">
            <Label htmlFor="resource">Ressource</Label>
            <Input
              id="resource"
              placeholder="Ex: arbre, user, inventaire..."
              value={filters.resource as string || ''}
              onChange={(e) =>
                onChange({ resource: e.target.value || undefined })
              }
            />
          </div>

          {/* Start Date Filter */}
          <div className="space-y-2">
            <Label>Date de début</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !startDate && 'text-muted-foreground'
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startDate ? (
                    format(startDate, 'PPP', { locale: fr })
                  ) : (
                    <span>Sélectionner une date</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={handleStartDateChange}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* End Date Filter */}
          <div className="space-y-2">
            <Label>Date de fin</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !endDate && 'text-muted-foreground'
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {endDate ? (
                    format(endDate, 'PPP', { locale: fr })
                  ) : (
                    <span>Sélectionner une date</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={handleEndDateChange}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
